/**
 * Vietnamese labels for system enums (DB-level).
 * Centralized here so any consumer (web, mobile, admin) gets consistent copy.
 *
 * Type definitions for these enums live in ../schemas (PostingState,
 * ApplicationState, KycStatus, UserStatus, GlobalRole, OrgRole). This file
 * intentionally does NOT re-declare those types to avoid duplicate exports;
 * import them from "@chotca/config" (re-exported from schemas) when you need
 * the type.
 *
 * `<PostingStateBadge>` etc. in @chotca/ui have their own internal label maps
 * including UI-extended states (e.g. "PENDING_PAYMENT", "FILLED") not in the
 * DB enum. This catalog is the source of truth for *raw DB enum values*.
 */
import type { MatchTier } from "./match";
import type {
  AccountType,
  ApplicationState,
  GlobalRole,
  KycStatus,
  OrgRole,
  PostingState,
  UserStatus,
} from "../schemas";

// ----------------------------------------------------------------------------
// Posting state (DB enum: PostingState)
// ----------------------------------------------------------------------------
export const POSTING_STATE_LABEL_VI: Record<PostingState, string> = {
  DRAFT: "Nháp",
  OPEN: "Đang tuyển",
  CLOSED: "Đã đóng",
  EXPIRED: "Hết hạn",
  CANCELLED: "Đã huỷ",
};

// ----------------------------------------------------------------------------
// Application state (DB enum: ApplicationState)
// ----------------------------------------------------------------------------
export const APPLICATION_STATE_LABEL_VI: Record<ApplicationState, string> = {
  APPLIED: "Đang chờ",
  INVITED: "Được mời phỏng vấn",
  ACCEPTED: "Đã chọn lịch phỏng vấn",
  COMMITTED: "Đã xác nhận phỏng vấn",
  REJECTED: "Không phù hợp",
  WITHDRAWN: "Đã rút",
  EXPIRED: "Hết hạn",
};

// ----------------------------------------------------------------------------
// KYC status (DB enum: KycStatus)
// ----------------------------------------------------------------------------
export const KYC_STATUS_LABEL_VI: Record<KycStatus, string> = {
  NONE: "Chưa xác minh",
  PENDING: "Đang duyệt",
  APPROVED: "Đã xác minh",
  REJECTED: "Bị từ chối",
  EXPIRED: "Đã hết hạn",
};

// ----------------------------------------------------------------------------
// User status (DB enum: UserStatus)
// ----------------------------------------------------------------------------
export const USER_STATUS_LABEL_VI: Record<UserStatus, string> = {
  ACTIVE: "Đang hoạt động",
  SUSPENDED: "Đã khoá",
  DELETED: "Đã xoá",
};

// ----------------------------------------------------------------------------
// Roles
// ----------------------------------------------------------------------------
export const GLOBAL_ROLE_LABEL_VI: Record<GlobalRole, string> = {
  USER: "Người dùng",
  ADMIN: "Quản trị viên",
};

export const ORG_ROLE_LABEL_VI: Record<OrgRole, string> = {
  OWNER: "Chủ doanh nghiệp",
  HR: "Nhân sự",
};

// Derived account type (not a DB enum) — see accountTypeSchema in schemas/users.
export const ACCOUNT_TYPE_LABEL_VI: Record<AccountType, string> = {
  WORKER: "Người lao động",
  EMPLOYER: "Nhà tuyển dụng",
  ADMIN: "Quản trị viên",
};

// ----------------------------------------------------------------------------
// Interview credit purchase status (DB enum: InterviewCreditPurchaseStatus)
// ----------------------------------------------------------------------------
export const INTERVIEW_CREDIT_PURCHASE_STATUSES = [
  "PENDING",
  "PAID",
  "EXPIRED",
  "FAILED",
] as const;
export type InterviewCreditPurchaseStatus =
  (typeof INTERVIEW_CREDIT_PURCHASE_STATUSES)[number];

export const INTERVIEW_CREDIT_PURCHASE_STATUS_LABEL_VI: Record<
  InterviewCreditPurchaseStatus,
  string
> = {
  PENDING: "Chờ thanh toán",
  PAID: "Đã thanh toán",
  EXPIRED: "Hết hạn",
  FAILED: "Thất bại",
};

// ----------------------------------------------------------------------------
// Language proficiency (used in postings + worker profile preferredLanguages)
// ----------------------------------------------------------------------------
export const LANGUAGE_LEVELS = ["BASIC", "INTERMEDIATE", "FLUENT"] as const;
export type LanguageLevel = (typeof LANGUAGE_LEVELS)[number];

export const LANGUAGE_LEVEL_LABEL_VI: Record<LanguageLevel, string> = {
  BASIC: "Cơ bản",
  INTERMEDIATE: "Trung cấp",
  FLUENT: "Thành thạo",
};

// Common language codes used in profile preferred-languages list (extensible)
export const COMMON_LANGUAGE_CODE_LABEL_VI: Record<string, string> = {
  EN: "Tiếng Anh",
  VI: "Tiếng Việt",
  KO: "Tiếng Hàn",
  JA: "Tiếng Nhật",
  ZH: "Tiếng Trung",
  FR: "Tiếng Pháp",
  TH: "Tiếng Thái",
};

// ----------------------------------------------------------------------------
// Match tier (type defined in ./match.ts; this is just the label map)
// ----------------------------------------------------------------------------
export const MATCH_TIER_LABEL_VI: Record<MatchTier, string> = {
  STRONG: "Rất phù hợp",
  MEDIUM: "Khá phù hợp",
  WEAK: "Ít phù hợp",
};

// Match check categories (used for breakdown display)
export const MATCH_CHECK_CATEGORY_LABEL_VI: Record<string, string> = {
  POSITION: "Vị trí",
  EMPLOYMENT_TYPE: "Loại hình",
  SALARY: "Mức lương",
  DISTANCE: "Khoảng cách",
  GENDER: "Giới tính",
  AGE: "Độ tuổi",
  HEIGHT: "Chiều cao",
  MOTORBIKE: "Xe máy",
  VISIBLE_TATTOO: "Hình xăm",
  KYC_CCCD: "Xác minh CCCD",
  EXPERIENCE: "Kinh nghiệm",
  SKILL_REQUIRED: "Kỹ năng bắt buộc",
  SKILL_PREFERRED: "Kỹ năng ưu tiên",
};
